import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Linkedin, Github, Twitter } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { personalInfo } from '../mock';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock form submission
    setTimeout(() => {
      toast({
        title: 'Message Sent!',
        description: 'Thank you for reaching out. I\'ll get back to you soon!',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: personalInfo.email,
      link: `mailto:${personalInfo.email}`
    },
    {
      icon: Phone,
      label: 'Phone',
      value: personalInfo.phone,
      link: `tel:${personalInfo.phone.replace(/\s/g, '')}`
    },
    {
      icon: MapPin,
      label: 'Location',
      value: personalInfo.location,
      link: null
    }
  ];

  const socialLinks = [
    { icon: Linkedin, label: 'LinkedIn', url: 'https://linkedin.com/in/alexmorgan' },
    { icon: Github, label: 'GitHub', url: 'https://github.com/alexmorgan' },
    { icon: Twitter, label: 'Twitter', url: 'https://twitter.com/alexmorgan' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-background via-background to-cyan-950/20 dark:to-cyan-950/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Get in <span className="text-cyan-400">Touch</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Have a project in mind or want to discuss opportunities? I'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="border-border">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold mb-6">
                    Send a <span className="text-cyan-400">Message</span>
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Your Name
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="border-cyan-500/30 focus:border-cyan-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="border-cyan-500/30 focus:border-cyan-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium mb-2">
                        Subject
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Project Inquiry"
                        className="border-cyan-500/30 focus:border-cyan-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">
                        Message
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell me about your project or inquiry..."
                        rows={6}
                        className="border-cyan-500/30 focus:border-cyan-500 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Methods */}
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  Contact <span className="text-cyan-400">Information</span>
                </h2>
                <div className="space-y-4">
                  {contactMethods.map((method, idx) => {
                    const Icon = method.icon;
                    return (
                      <Card
                        key={idx}
                        className="border-border hover:border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/10"
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-cyan-500/10 text-cyan-400">
                              <Icon className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold mb-1">{method.label}</h3>
                              {method.link ? (
                                <a
                                  href={method.link}
                                  className="text-muted-foreground hover:text-cyan-400 transition-colors"
                                >
                                  {method.value}
                                </a>
                              ) : (
                                <p className="text-muted-foreground">{method.value}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Social Links */}
              <Card className="border-border bg-gradient-to-br from-cyan-500/10 to-blue-500/10">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4 text-cyan-400">
                    Connect with Me
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Follow me on social media for updates and insights.
                  </p>
                  <div className="flex gap-4">
                    {socialLinks.map((social, idx) => {
                      const Icon = social.icon;
                      return (
                        <a
                          key={idx}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 rounded-lg bg-background/50 border border-cyan-500/30 hover:border-cyan-500 hover:bg-cyan-500/10 transition-all hover:scale-110"
                          aria-label={social.label}
                        >
                          <Icon className="h-6 w-6 text-cyan-400" />
                        </a>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Newsletter */}
              <Card className="border-border">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4 text-cyan-400">
                    Newsletter
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Subscribe to receive updates on new projects and blog posts.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Your email"
                      className="border-cyan-500/30 focus:border-cyan-500"
                    />
                    <Button
                      onClick={() => alert('Newsletter signup will be implemented with backend!')}
                      className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                    >
                      Subscribe
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;